/**
 * Tiny module-scoped store + fetchers for the per-user preferences
 * surfaced in the Sidebar settings section.
 *
 * Single field for V0 — ``email_notifications_enabled``. Optimistic
 * UI on toggle: flip the local value immediately, fire PUT, roll
 * back + toast on 5xx so the user isn't held by a 200 ms backend
 * round-trip for a checkbox.
 */
import { ref } from "vue";
import { useAuth } from "./useAuth";
import { readUserFacingError } from "../errorMessages";

export interface Preferences {
  email_notifications_enabled: boolean;
}

const DEFAULTS: Preferences = { email_notifications_enabled: true };

const preferences = ref<Preferences>({ ...DEFAULTS });
const loadError = ref<string | null>(null);
const saveError = ref<string | null>(null);

async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const resp = await fetch(input, { credentials: "include", ...init });
  if (resp.status === 401) {
    const { signOut } = useAuth();
    await signOut();
  }
  return resp;
}

async function loadPreferences(): Promise<void> {
  loadError.value = null;
  try {
    const resp = await apiFetch("/api/me/preferences");
    if (resp.status === 401) {
      // signOut already fired; the router guard will route to /login.
      preferences.value = { ...DEFAULTS };
      return;
    }
    if (!resp.ok) {
      // Minimal migration: take only `.message`. Render of the
      // Reference: trace_id line in PreferencesView is a follow-up
      // (not in the error-correlation-one-pager.md caller matrix).
      loadError.value = (await readUserFacingError(resp)).message;
      return;
    }
    const body = (await resp.json()) as Preferences;
    preferences.value = {
      // Spread defaults underneath so a future server-side rename
      // doesn't leave the local store with undefined fields.
      ...DEFAULTS,
      ...body,
    };
  } catch (err) {
    loadError.value = `Cannot reach backend: ${(err as Error).message}`;
  }
}

/**
 * Toggle and persist the email-notifications preference.
 *
 * Optimistic: the local value flips immediately so the checkbox feels
 * responsive. On a non-2xx response we roll back AND surface
 * ``saveError`` so the Sidebar can show a toast — the user's intent
 * wasn't honored.
 */
async function setEmailNotificationsEnabled(value: boolean): Promise<void> {
  saveError.value = null;
  const previous = preferences.value.email_notifications_enabled;
  preferences.value = { ...preferences.value, email_notifications_enabled: value };
  try {
    const resp = await apiFetch("/api/me/preferences", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email_notifications_enabled: value }),
    });
    if (resp.status === 401) {
      // signOut fired; the router will navigate away. Roll back so a
      // landed-on-/login state doesn't leak the optimistic write.
      preferences.value = { ...preferences.value, email_notifications_enabled: previous };
      return;
    }
    if (!resp.ok) {
      preferences.value = { ...preferences.value, email_notifications_enabled: previous };
      saveError.value = (await readUserFacingError(resp)).message;
      return;
    }
    const body = (await resp.json()) as Preferences;
    preferences.value = { ...DEFAULTS, ...body };
  } catch (err) {
    preferences.value = { ...preferences.value, email_notifications_enabled: previous };
    saveError.value = `Cannot reach backend: ${(err as Error).message}`;
  }
}

function _resetForTests(): void {
  preferences.value = { ...DEFAULTS };
  loadError.value = null;
  saveError.value = null;
}

export function usePreferences() {
  return {
    preferences,
    loadError,
    saveError,
    loadPreferences,
    setEmailNotificationsEnabled,
    _resetForTests,
  };
}
